/**
 * BusinessHoursSelector
 * Weekly business hours picker with per-day open/closed toggle,
 * start/end time dropdowns (30-min increments), "Copy to all weekdays", and 24/7 option.
 */
import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

function generateTimeSlots() {
  const slots = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      const hh = String(h).padStart(2, '0');
      const mm = String(m).padStart(2, '0');
      const value = `${hh}:${mm}`;
      const period = h < 12 ? 'AM' : 'PM';
      const displayH = h === 0 ? 12 : h > 12 ? h - 12 : h;
      const label = `${displayH}:${mm} ${period}`;
      slots.push({ value, label });
    }
  }
  return slots;
}

const TIME_SLOTS = generateTimeSlots();

export default function BusinessHoursSelector({ hours, onChange, error }) {
  const is247 = useMemo(() => {
    return DAYS.every((day) => {
      const d = hours[day];
      return d && !d.closed && d.open === '00:00' && d.close === '23:30';
    });
  }, [hours]);

  const handleToggleClosed = useCallback((day) => {
    onChange({
      ...hours,
      [day]: { ...hours[day], closed: !hours[day].closed },
    });
  }, [hours, onChange]);

  const handleTimeChange = useCallback((day, field, value) => {
    onChange({
      ...hours,
      [day]: { ...hours[day], [field]: value },
    });
  }, [hours, onChange]);

  const handleCopyToWeekdays = useCallback(() => {
    const monday = hours['Monday'];
    if (!monday) return;
    const updated = { ...hours };
    WEEKDAYS.forEach((day) => {
      updated[day] = { ...monday };
    });
    onChange(updated);
  }, [hours, onChange]);

  const handleToggle247 = useCallback(() => {
    if (is247) {
      // Reset to default business hours
      const updated = {};
      DAYS.forEach((day) => {
        const isWeekend = day === 'Saturday' || day === 'Sunday';
        updated[day] = { open: '09:00', close: '17:00', closed: isWeekend };
      });
      onChange(updated);
    } else {
      // Set to 24/7
      const updated = {};
      DAYS.forEach((day) => {
        updated[day] = { open: '00:00', close: '23:30', closed: false };
      });
      onChange(updated);
    }
  }, [is247, onChange]);

  return (
    <div>
      {error && <div style={{ fontSize: '12px', color: '#ef4444', marginBottom: '12px' }}>{error}</div>}

      {/* Quick actions */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={handleToggle247}
          style={{
            padding: '6px 14px',
            fontSize: '13px',
            fontWeight: '600',
            borderRadius: '8px',
            border: is247 ? '2px solid #3b82f6' : '1px solid #d1d5db',
            backgroundColor: is247 ? '#eff6ff' : '#fff',
            color: is247 ? '#1e40af' : '#475569',
            cursor: 'pointer',
          }}
        >
          24/7 Open
        </button>
        <button
          type="button"
          onClick={handleCopyToWeekdays}
          style={{
            padding: '6px 14px',
            fontSize: '13px',
            fontWeight: '600',
            borderRadius: '8px',
            border: '1px solid #d1d5db',
            backgroundColor: '#fff',
            color: '#475569',
            cursor: 'pointer',
          }}
        >
          Copy Monday to All Weekdays
        </button>
      </div>

      {/* Day rows */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {DAYS.map((day) => {
          const dayHours = hours[day] || { open: '09:00', close: '17:00', closed: false };
          return (
            <div
              key={day}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '8px 12px',
                backgroundColor: dayHours.closed ? '#f8fafc' : '#fff',
                borderRadius: '8px',
                border: '1px solid #f1f5f9',
                flexWrap: 'wrap',
              }}
            >
              {/* Day name */}
              <div style={{
                width: '90px',
                fontSize: '14px',
                fontWeight: '600',
                color: dayHours.closed ? '#94a3b8' : '#1e293b',
                flexShrink: 0,
              }}>
                {day}
              </div>

              {/* Open/Closed toggle */}
              <button
                type="button"
                onClick={() => handleToggleClosed(day)}
                style={{
                  padding: '4px 12px',
                  fontSize: '12px',
                  fontWeight: '600',
                  borderRadius: '6px',
                  border: 'none',
                  cursor: 'pointer',
                  backgroundColor: dayHours.closed ? '#fef2f2' : '#f0fdf4',
                  color: dayHours.closed ? '#991b1b' : '#166534',
                  minWidth: '60px',
                }}
              >
                {dayHours.closed ? 'Closed' : 'Open'}
              </button>

              {/* Time selectors */}
              {!dayHours.closed && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                  <select
                    value={dayHours.open}
                    onChange={(e) => handleTimeChange(day, 'open', e.target.value)}
                    style={timeSelectStyle}
                  >
                    {TIME_SLOTS.map((slot) => (
                      <option key={slot.value} value={slot.value}>{slot.label}</option>
                    ))}
                  </select>
                  <span style={{ color: '#94a3b8', fontSize: '13px' }}>to</span>
                  <select
                    value={dayHours.close}
                    onChange={(e) => handleTimeChange(day, 'close', e.target.value)}
                    style={timeSelectStyle}
                  >
                    {TIME_SLOTS.map((slot) => (
                      <option key={slot.value} value={slot.value}>{slot.label}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

BusinessHoursSelector.propTypes = {
  hours: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string,
};

const timeSelectStyle = {
  padding: '6px 8px',
  fontSize: '13px',
  borderRadius: '6px',
  border: '1px solid #d1d5db',
  backgroundColor: '#fff',
  color: '#1e293b',
  cursor: 'pointer',
  outline: 'none',
};
